import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import {
  EmptyState,
  ErrorBox,
  Loader,
  PageHeader,
  formatDateTime,
} from '../components/Common';

export function WaitlistPage() {
  const queryClient = useQueryClient();
  const [masterId, setMasterId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [date, setDate] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['waitlist'],
    queryFn: () => api.waitlist.list(),
  });

  const masters = useQuery({ queryKey: ['masters'], queryFn: api.masters.list });
  const services = useQuery({ queryKey: ['services'], queryFn: api.services.listPublic });

  const subscribe = useMutation({
    mutationFn: () =>
      api.waitlist.subscribe({
        master_id: masterId,
        service_id: serviceId || undefined,
        desired_date: date || undefined,
      }),
    onSuccess: () => {
      setMasterId('');
      setServiceId('');
      setDate('');
      void queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });

  const unsubscribe = useMutation({
    mutationFn: (id: string) => api.waitlist.unsubscribe(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });

  return (
    <div>
      <PageHeader
        title="Лист ожидания"
        subtitle="Получайте уведомления о свободных слотах"
      />

      <div className="card mb-4 space-y-3">
        <p className="text-sm font-semibold">Подписаться на освобождение</p>
        <div>
          <label className="label">Мастер</label>
          <select
            className="input"
            value={masterId}
            onChange={(e) => setMasterId(e.target.value)}
          >
            <option value="">— выберите —</option>
            {masters.data?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.first_name} {m.last_name ?? ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Услуга (необязательно)</label>
          <select
            className="input"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
          >
            <option value="">любая</option>
            {services.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Желаемая дата (необязательно)</label>
          <input
            type="date"
            className="input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        {subscribe.error && (
          <ErrorBox message={(subscribe.error as Error).message} />
        )}
        <button
          className="btn-primary w-full"
          disabled={!masterId || subscribe.isPending}
          onClick={() => subscribe.mutate()}
        >
          {subscribe.isPending ? 'Подписываем...' : 'Подписаться'}
        </button>
      </div>

      <h2 className="mb-2 text-sm font-semibold text-tg-hint">Ваши подписки</h2>
      {isLoading && <Loader />}
      {error && <ErrorBox message={(error as Error).message} onRetry={() => void refetch()} />}
      {!isLoading && data && data.length === 0 && (
        <EmptyState icon="⏳" title="Подписок нет" />
      )}

      <ul className="space-y-2">
        {data?.map((w) => (
          <li key={w.id} className="card flex items-center justify-between">
            <div className="text-xs text-tg-hint">
              <p>Создана: {formatDateTime(w.created_at)}</p>
              <p>Активна до: {formatDateTime(w.expires_at)}</p>
            </div>
            <button
              className="btn-secondary"
              disabled={unsubscribe.isPending}
              onClick={() => unsubscribe.mutate(w.id)}
            >
              Отписаться
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
